import React, { useCallback, useState, useEffect } from 'react';
import {
  getAllCompanies,
  getStateAndCity,
  getLandById
} from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { uploadFile, multipleUploadFiles } from '../../../builder/features/files';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const EditLand = ({ onClose, onSubmit, id, isSubmitting }) => {
  const userId = Cookies.get('userId');
  const dispatch = useDispatch();

  // Fetch land data
  const { data: land, isLoading } = useQuery({
    queryKey: ['land', id],
    queryFn: () => getLandById(id),
    enabled: !!id
  });

  // Fetch companies data
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getAllCompanies()
  });

  // Initialize form methods
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      total_mainAreaType: 'sq/hectare',
      total_mainArea: 0,
      total_subArea: 0,
      sub_area_measuring_unit: 'none'
    }
  });

  // Initialize watchers early
  const watchedSubAreaUnit = watch('sub_area_measuring_unit', 'none');

  // Local state
  const [owners, setOwners] = useState([]);
  const [state, setState] = useState('-');
  const [district, setDistrict] = useState('Select');
  const [tehsil, setTehsil] = useState('Select');
  const [city, setCity] = useState('Select');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);
  const [districtDropdownOptions, setDistrictDropdownOptions] = useState([]);
  const [tehsilDropdownOptions, setTehsilDropdownOptions] = useState([]);
  const [totalMainArea, setTotalMainArea] = useState(0);
  const [totalSubArea, setTotalSubArea] = useState(0);
  const [totalMainAreaType, setTotalMainAreaType] = useState('sq/hectare'); // Default unit
  const [totalSubAreaType, setTotalSubAreaType] = useState('sq/gunta'); // Fixed unit
  const [previousSubAreas, setPreviousSubAreas] = useState({}); // Store previous sub-area values

  // Redux selectors for file URLs
  const fileUrls = useSelector((state) => state.file.fileUrls);
  const MultipleFiles = useSelector((state) => state.file.multifiles);

  // Initialize owners and form values when land data is fetched

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Land Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (land) {
      console.log('land details', land);

      // Set measuring units first
      setValue('total_mainAreaType', land.total_mainAreaType || 'sq/hectare');
      setValue(
        'sub_area_measuring_unit',
        land.total_subAreaType === 'sq/gunta'
          ? 'guntha'
          : land.total_subAreaType === 'sq/cents'
            ? 'cents'
            : land.total_subAreaType === 'none'
              ? 'none'
              : 'none'
      );

      const initialOwners = land.ownerLandMappings.map((ownerMapping, ownerIndex) => ({
        ownerId: ownerMapping.owner.id,
        khasras: ownerMapping.owner.ownerKhasraMappings.map((khasraMapping, khasraIndex) => {
          const khasra = khasraMapping.khasra;
          const subArea = khasra.subArea || 0;
          // Set the sub-area value in the form
          setValue(`subArea-${ownerIndex}-${khasraIndex}-${Date.now()}`, subArea.toString());
          return {
            khasraId: `${ownerIndex}-${khasraIndex}-${Date.now()}`,
            khasra_number: khasra.number,
            mainArea: khasra.mainArea,
            subArea: subArea,
            khasra_map: khasra.khasra_map_link,
            khasra_p1b2: khasra.khasra_p1b2_link,
            land_use_map: khasra.land_use_map
          };
        })
      }));

      setOwners(initialOwners);

      // Store initial sub-area values and set form values
      const initialSubAreas = {};
      initialOwners.forEach((owner) => {
        owner.khasras.forEach((khasra) => {
          initialSubAreas[khasra.khasraId] = khasra.subArea;
          setValue(`subArea-${khasra.khasraId}`, khasra.subArea.toString());
        });
      });
      setPreviousSubAreas(initialSubAreas);

      // Set form field values for owners and khasras
      initialOwners.forEach((owner, ownerIndex) => {
        const ownerData = land.ownerLandMappings[ownerIndex].owner;

        setValue(`name-${owner.ownerId}`, ownerData.name);
        setValue(`email-${owner.ownerId}`, ownerData.email);
        setValue(`phone_number-${owner.ownerId}`, ownerData.phone_number);
        setValue(`aadhar-${owner.ownerId}`, ownerData.aadhar);
        setValue(`pan-${owner.ownerId}`, ownerData.pan_number);

        if (ownerData.diversionOrderFiles) {
          // Extract URLs from diversionOrderFiles
          const diversionOrderURLs = ownerData.diversionOrderFiles.map((file) => ({
            name: file.name,
            url: file.url
          }));
          dispatch(
            multipleUploadFiles({
              fileType: `diversion_order-${owner.ownerId}`,
              urls: diversionOrderURLs
            })
          );
        }

        owner.khasras.forEach((khasra) => {
          setValue(`khasra_number-${khasra.khasraId}`, khasra.khasra_number);
          setValue(`mainArea-${khasra.khasraId}`, khasra.mainArea);
          setValue(`subArea-${khasra.khasraId}`, khasra.subArea);

          if (khasra.khasra_map) {
            dispatch(
              uploadFile({ fileType: `khasra_map-${khasra.khasraId}`, url: khasra.khasra_map })
            );
          }

          if (khasra.khasra_p1b2) {
            dispatch(
              uploadFile({ fileType: `khasra_p1b2-${khasra.khasraId}`, url: khasra.khasra_p1b2 })
            );
          }

          if (khasra.land_use_map) {
            dispatch(
              uploadFile({ fileType: `land_use_map-${khasra.khasraId}`, url: khasra.land_use_map })
            );
          }
        });
      });

      // Set other land details
      Object.entries(land).forEach(([key, value]) => {
        setValue(key, value);
      });

      if (land.pincode) {
        handlePincodeChange(land.pincode);
      }

      if (land.company_id && companies?.length > 0) {
        const selectedCompany = companies.find(
          (item) => parseInt(item.id) === parseInt(land.company_id)
        );
        setValue('company_id', selectedCompany?.id || '');
      }

      // Pre-fill additional document files
      dispatch(
        multipleUploadFiles({
          fileType: 'registry_document',
          urls: land.registryDocs?.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
      dispatch(
        multipleUploadFiles({
          fileType: 'advocate_document',
          urls: land.advocateSearches?.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
      dispatch(
        multipleUploadFiles({
          fileType: 'land_map',
          urls: land.additionalDocuments?.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
    }
  }, [land, setValue, companies, dispatch]);

  // Pincode change handler
  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.toString().replace(/[^0-9]/g, '');
      setValue('pincode', cleanedPincode);
      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0]?.PostOffice || [];
          if (postOfficeList.length > 0) {
            const fetchedState = postOfficeList[0].State;
            const uniqueDistricts = [...new Set(postOfficeList.map((office) => office.District))];
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];
            const uniqueCities = [...new Set(postOfficeList.map((office) => office.Name))];

            setState(fetchedState);
            setDistrict(uniqueDistricts[0]);
            setTehsil(uniqueTehsils[0]);
            setCity(uniqueCities[0]);

            setDistrictDropdownOptions(
              uniqueDistricts.map((district) => ({
                value: district,
                label: district
              }))
            );
            setTehsilDropdownOptions(
              uniqueTehsils.map((tehsil) => ({
                value: tehsil,
                label: tehsil
              }))
            );
            setCityDropdownOptions(
              uniqueCities.map((city) => ({
                value: city,
                label: city
              }))
            );

            setValue('state', fetchedState);
            setValue('district', uniqueDistricts[0]);
            setValue('tehsil', uniqueTehsils[0]);
            setValue('city', uniqueCities[0]);
          } else {
            resetLocationFields();
          }
        } catch (error) {
          toast.error('Invalid pincode. Please enter a valid pincode.');
          resetLocationFields();
        }
      }
    },
    [setValue]
  );

  const resetLocationFields = () => {
    setValue('state', '-');
    setValue('district', 'Select');
    setValue('tehsil', 'Select');
    setValue('city', 'Select');
    setState('-');
    setDistrict('Select');
    setTehsil('Select');
    setCity('Select');
    setDistrictDropdownOptions([]);
    setTehsilDropdownOptions([]);
    setCityDropdownOptions([]);
  };

  // Handlers for area changes
  const handleMainAreaChange = useCallback(
    (ownerId, khasraId, event) => {
      // Get the value from the event object
      const value = event?.target?.value ?? '';

      // Clean the input value to allow only numbers and decimals
      const cleanedValue = value.replace(/[^0-9.]/g, '').slice(0, 8);

      // Update the form value
      setValue(`mainArea-${khasraId}`, cleanedValue);

      // Convert to number for calculations
      const numericValue = Number(cleanedValue) || 0;

      // Update owners state
      setOwners((prevOwners) => {
        const newOwners = prevOwners.map((owner) => {
          if (owner.ownerId === ownerId) {
            const newKhasras = owner.khasras.map((khasra) => {
              if (khasra.khasraId === khasraId) {
                return { ...khasra, mainArea: numericValue };
              }
              return khasra;
            });
            return { ...owner, khasras: newKhasras };
          }
          return owner;
        });

        // Calculate new total main area
        const newTotalMainArea = newOwners.reduce((total, owner) => {
          return (
            total + owner.khasras.reduce((sum, khasra) => sum + (Number(khasra.mainArea) || 0), 0)
          );
        }, 0);

        // Update total main area
        setTotalMainArea(newTotalMainArea);

        return newOwners;
      });
    },
    [setValue]
  );

  const handleSubAreaChange = useCallback(
    (ownerId, khasraId, event) => {
      if (watchedSubAreaUnit === 'none') return;

      // Get the value from the event object
      const value = event?.target?.value ?? '';

      // Clean the input value to allow only numbers and decimals
      const cleanedValue = value.replace(/[^0-9.]/g, '').slice(0, 8);

      // Update the form value first
      setValue(`subArea-${khasraId}`, cleanedValue);

      // Convert to number for calculations
      const numericValue = parseFloat(cleanedValue) || 0;

      // Update owners state without recalculating immediately
      setOwners((prevOwners) =>
        prevOwners.map((owner) =>
          owner.ownerId === ownerId
            ? {
              ...owner,
              khasras: owner.khasras.map((khasra) =>
                khasra.khasraId === khasraId ? { ...khasra, subArea: numericValue } : khasra
              )
            }
            : owner
        )
      );

      // Update total separately to avoid interference
      setTimeout(() => {
        setTotalSubArea((prev) => {
          const newTotal = owners.reduce(
            (total, owner) =>
              total +
              owner.khasras.reduce(
                (sum, khasra) =>
                  khasra.khasraId === khasraId
                    ? sum + numericValue
                    : sum + (Number(khasra.subArea) || 0),
                0
              ),
            0
          );
          return newTotal;
        });
      }, 0);
    },
    [setValue, watchedSubAreaUnit, owners]
  );

  // Update total main and sub areas whenever owners state changes
  useEffect(() => {
    const totalMain = owners.reduce(
      (sum, owner) =>
        sum + owner.khasras.reduce((kSum, khasra) => kSum + (parseFloat(khasra.mainArea) || 0), 0),
      0
    );
    const totalSub = owners.reduce(
      (sum, owner) =>
        sum + owner.khasras.reduce((kSum, khasra) => kSum + (Number(khasra.subArea) || 0), 0),
      0
    );
    setTotalMainArea(totalMain);
    setTotalSubArea(totalSub);
  }, [owners]);

  // Handlers to add/remove owners and khasras
  const handleAddKhasraBlock = useCallback((ownerId) => {
    setOwners((prevOwners) =>
      prevOwners.map((owner) =>
        owner.ownerId === ownerId
          ? {
            ...owner,
            khasras: [
              ...owner.khasras,
              {
                khasraId: `${ownerId}-${Date.now()}`,
                khasra_number: '',
                mainArea: '',
                subArea: '',
                khasra_map: '',
                khasra_p1b2: '',
                land_use_map: ''
              }
            ]
          }
          : owner
      )
    );
  }, []);

  const handleRemoveKhasraBlock = useCallback((ownerId, khasraId) => {
    setOwners((prevOwners) =>
      prevOwners.map((owner) =>
        owner.ownerId === ownerId
          ? {
            ...owner,
            khasras: owner.khasras.filter((khasra) => khasra.khasraId !== khasraId)
          }
          : owner
      )
    );
  }, []);

  const handleAddOwnerBlock = useCallback(() => {
    setOwners((prevOwners) => [
      ...prevOwners,
      {
        ownerId: `new-${Date.now()}`,
        khasras: [
          {
            khasraId: `${Date.now()}`,
            khasra_number: '',
            mainArea: '',
            subArea: '',
            khasra_map: '',
            khasra_p1b2: '',
            land_use_map: ''
          }
        ]
      }
    ]);
  }, []);

  const handleRemoveOwnerBlock = useCallback((ownerId) => {
    setOwners((prevOwners) => prevOwners.filter((owner) => owner.ownerId !== ownerId));
  }, []);

  // Handlers for other input changes
  const handleAadharChange = useCallback(
    (aadhar, key) => {
      const cleanedAadhar = aadhar.replace(/[^0-9]/g, '').slice(0, 12); // Aadhar card is 12 digits
      setValue(key, cleanedAadhar);
    },
    [setValue]
  );

  const handlePhoneNumber = useCallback(
    (val, key) => {
      const cleanedValue = val.replace(/[^0-9]/g, '').slice(0, 10);
      setValue(key, cleanedValue);
    },
    [setValue]
  );

  const handleLandName = useCallback(
    (val) => {
      const newVal = val.replace(/[^a-zA-Z\s]/g, ''); // Only allow alphabets and spaces
      setValue('name', newVal);
    },
    [setValue]
  );

  const handleOwnerName = useCallback(
    (val, key) => {
      const newVal = val.replace(/[^a-zA-Z\s]/g, ''); // Only allow alphabets and spaces
      setValue(key, newVal);
    },
    [setValue]
  );

  const handlePanChange = useCallback(
    (pan, key) => {
      const cleanedPan = pan.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setValue(key, cleanedPan);
    },
    [setValue]
  );

  const handleLocalityChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
      setValue('area', cleanedValue);
    },
    [setValue]
  );

  // Watchers
  const watchedMeasuringUnit = useWatch({
    control,
    name: 'total_mainAreaType',
    defaultValue: 'sq/hectare'
  });

  const watchedStateChange = useWatch({
    control,
    name: 'state'
  });

  const [watchState, setWatchState] = useState(null);

  useEffect(() => {
    setTotalMainAreaType(watchedMeasuringUnit);
  }, [watchedMeasuringUnit]);

  useEffect(() => {
    if (watchedSubAreaUnit === 'none') {
      setTotalSubAreaType('none');
    } else if (watchedSubAreaUnit === 'guntha') {
      setTotalSubAreaType('guntha');
    } else if (watchedSubAreaUnit === 'cents') {
      setTotalSubAreaType('cents');
    }
  }, [watchedSubAreaUnit]);

  useEffect(() => {
    setWatchState(watchedStateChange?.toLowerCase());
  }, [watchedStateChange]);

  // Effect to handle sub area unit changes
  useEffect(() => {
    if (!owners.length) return;

    const isNone = watchedSubAreaUnit === 'none';

    if (isNone) {
      // Store current values before clearing
      const newPreviousSubAreas = {};
      owners.forEach((owner) => {
        owner.khasras.forEach((khasra) => {
          const currentValue = watch(`subArea-${khasra.khasraId}`);
          newPreviousSubAreas[khasra.khasraId] = Number(currentValue) || 0;
        });
      });
      setPreviousSubAreas(newPreviousSubAreas);

      // Clear all sub-area values
      setOwners((prevOwners) => {
        const newOwners = prevOwners.map((owner) => ({
          ...owner,
          khasras: owner.khasras.map((khasra) => {
            setValue(`subArea-${khasra.khasraId}`, '');
            return { ...khasra, subArea: 0 };
          })
        }));
        return newOwners;
      });

      setTotalSubArea(0);
    } else if (Object.keys(previousSubAreas).length > 0) {
      // Only restore values when switching from 'none' to 'guntha'
      setOwners((prevOwners) => {
        const newOwners = prevOwners.map((owner) => ({
          ...owner,
          khasras: owner.khasras.map((khasra) => {
            const previousValue = previousSubAreas[khasra.khasraId] || 0;
            setValue(`subArea-${khasra.khasraId}`, previousValue.toString());
            return { ...khasra, subArea: previousValue };
          })
        }));
        return newOwners;
      });

      // Recalculate total sub area
      const newTotalSubArea = Object.values(previousSubAreas).reduce(
        (sum, value) => sum + (Number(value) || 0),
        0
      );
      setTotalSubArea(newTotalSubArea);
    }
  }, [watchedSubAreaUnit]); // Remove owners from dependency array and only run when unit changes

  // Form submission handler
  const handleOnSubmitEditLand = (formData) => {
    const transformedData = {
      owner: owners.map((owner) => {
        // Create base owner object with required fields
        const ownerData = {
          name: formData[`name-${owner.ownerId}`],
          phone_number: formData[`phone_number-${owner.ownerId}`],
          pan_number: formData[`pan-${owner.ownerId}`],
          diversion_order_link: MultipleFiles[`diversion_order-${owner.ownerId}`] || [],
          // Add khasra data immediately to ensure it's always included
          khasra: owner.khasras.map((khasra) => ({
            number: formData[`khasra_number-${khasra.khasraId}`],
            mainArea: Number(formData[`mainArea-${khasra.khasraId}`]) || 0,
            subArea:
              formData.sub_area_measuring_unit === 'none'
                ? 0
                : Number(formData[`subArea-${khasra.khasraId}`]) || 0,
            mainAreaType: formData.total_mainAreaType,
            subAreaType:
              formData.sub_area_measuring_unit === 'none'
                ? 'none'
                : formData.sub_area_measuring_unit === 'cents'
                  ? 'sq/cents'
                  : 'sq/gunta',
            khasra_map_link: fileUrls[`khasra_map-${khasra.khasraId}`] || [],
            khasra_p1b2_link: fileUrls[`khasra_p1b2-${khasra.khasraId}`] || [],
            land_use_map: fileUrls[`land_use_map-${khasra.khasraId}`] || []
          }))
        };

        // Conditionally add the id if it exists and is not a temporary ID
        if (typeof owner.ownerId === 'number') {
          ownerData.id = owner.ownerId;
        }

        // Only add email if it's not empty
        if (formData[`email-${owner.ownerId}`]?.trim()) {
          ownerData.email = formData[`email-${owner.ownerId}`];
        }

        // Only add aadhar if it's not empty
        if (formData[`aadhar-${owner.ownerId}`]?.trim()) {
          ownerData.aadhar = formData[`aadhar-${owner.ownerId}`];
        }

        return ownerData;
      }),
      name: formData.name,
      company_id: parseInt(formData.company_id),
      registry_doc_link: MultipleFiles?.registry_document || [],
      advocate_search_link: MultipleFiles?.advocate_document || [],
      measuringUnit: formData.total_mainAreaType,
      total_mainArea: totalMainArea,
      total_subArea: formData.sub_area_measuring_unit === 'none' ? 0 : totalSubArea,
      total_mainAreaType: formData.total_mainAreaType,
      total_subAreaType:
        formData.sub_area_measuring_unit === 'none'
          ? 'none'
          : formData.sub_area_measuring_unit === 'cents'
            ? 'sq/cents'
            : 'sq/gunta',
      pincode: parseInt(formData.pincode),
      state: formData.state,
      district: formData.district,
      tehsil: formData.tehsil,
      city: formData.city,
      area: formData.area || '',
      google_map_link: formData.google_map_link || '',
      additional_documents_link: MultipleFiles?.land_map || []
    };

    console.log('Submitting transformed data:', transformedData);
    onSubmit(transformedData);
  };

  const formatAreaType = (areaType) => {
    return areaType?.replace('sq/', '');
  };

  // Helper function to check if state uses Survey terminology
  const isSurveyState = (state) => {
    const surveyStates = [
      'andhra pradesh',
      'telangana',
      'karnataka',
      'kerala',
      'tamil nadu',
      'gujarat'
    ];
    return surveyStates.includes(state?.toLowerCase());
  };

  // Helper function to check if state uses Dag terminology
  const isDagState = (state) => {
    return state?.toLowerCase() === 'west bengal';
  };

  const getLandLabel = (state) => {
    if (isDagState(state)) return 'Dag';
    if (isSurveyState(state)) return 'Survey';
    return 'Khasra';
  };

  if (isLoading) {
    return <div>Loading...</div>; // You can replace this with a spinner
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleOnSubmitEditLand)}>
      {/* Land Details Section */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Land Details</span>

        <div className="flex flex-col w-full flex-1 gap-4">
          <div className="flex space-x-8 justify-between items-start w-full">
            <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
              <div className="w-full flex flex-col gap-4 flex-1">
                <div className="flex justify-between space-x-4">
                  <FormInput
                    width={32}
                    register={register}
                    isRequired={true}
                    register_key={'name'}
                    label={'Land Name'}
                    type={'text'}
                    onType={handleLandName}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={'company_id'}
                    label={'Company name'}
                    type={'dropdown'}
                    options={[
                      { value: '', label: 'Select Company' },
                      ...(companies
                        ? companies.map((item) => ({
                          value: item.id,
                          label: item.company_name.trim()
                        }))
                        : [])
                    ]}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={'registry_document'}
                    label={'Registry Document'}
                    type={'file-secondary'}
                    prefilledFiles={MultipleFiles['registry_document'] || []}
                  />
                </div>
              </div>

              <div className="w-full flex justify-between space-x-4 items-center pb-6">
                <FormInput
                  width={32}
                  register={register}
                  register_key={'advocate_document'}
                  label={'Advocate Search'}
                  type={'file-secondary'}
                  prefilledFiles={MultipleFiles['advocate_document'] || []}
                />

                <div className="flex flex-col w-[32%]">
                  <label className="text-md text-gray-600 mb-2">
                    {' '}
                    {getLandLabel(watchState)} Area Measurement Type
                  </label>

                  <div className="flex items-center justify-between gap-4">
                    {/* Main area measuring unit */}
                    <select
                      className="border border-gray-300 rounded-md px-3 py-2 w-1/2"
                      {...register('total_mainAreaType', { required: true })}
                      defaultValue={land?.total_mainAreaType || 'sq/hectare'}>
                      <option value="sq/hectare">Hectares</option>
                      <option value="sq/acres">Acres</option>
                    </select>

                    {/* Sub area measuring unit */}
                    <select
                      className="border border-gray-300 rounded-md px-3 py-2 w-1/2"
                      {...register('sub_area_measuring_unit', { required: true })}
                      defaultValue={
                        land?.total_subAreaType === 'none'
                          ? 'none'
                          : land?.total_subAreaType === 'sq/cents'
                            ? 'cents'
                            : 'guntha'
                      }>
                      <option value="none">None</option>
                      <option value="guntha">Guntha</option>
                      <option value="cents">Cents</option>
                    </select>
                  </div>

                  {/* Error messages (optional) */}
                  {errors.total_mainAreaType && (
                    <p className="text-red-500 text-sm mt-1">{errors.total_mainAreaType.message}</p>
                  )}
                  {errors.sub_area_measuring_unit && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.sub_area_measuring_unit.message}
                    </p>
                  )}
                </div>
                <div className="flex w-[32%] gap-4 items-center">
                  <FormInput
                    width={55}
                    register={register}
                    isRequired={true}
                    register_key={'total_main_area'}
                    label={`Total ${getLandLabel(watchState)} area (${formatAreaType(watchedMeasuringUnit)})`}
                    type={'text'}
                    valuePre={totalMainArea || 0}
                    CustomStyle={{ pointerEvents: 'none', backgroundColor: '#E7E5E4' }}
                  />
                  <FormInput
                    width={40}
                    register={register}
                    isRequired={true}
                    register_key={'total_sub_area'}
                    label={`Total${totalSubAreaType === 'guntha'
                        ? ' (guntha)'
                        : totalSubAreaType === 'cents'
                          ? ' (cents)'
                          : ''
                      }`}
                    type={'textOnType'}
                    valuePre={totalSubArea || 0}
                    CustomStyle={{ pointerEvents: 'none', backgroundColor: '#E7E5E4' }}
                  />
                </div>
              </div>

              <div className="w-full flex justify-between space-x-4 items-center">
                <FormInput
                  width={32}
                  register={register}
                  isRequired={true}
                  register_key={'pincode'}
                  label={'Pincode'}
                  type={'text'}
                  maxLength={6}
                  onType={handlePincodeChange}
                />

                <FormInput
                  width={32}
                  register={register}
                  register_key={'land_use_map'}
                  label={'Land use map'}
                  type={'hidden'}
                  prefilledFiles={fileUrls['land_use_map'] || []}
                />

                <FormInput
                  width={32}
                  register={register}
                  isRequired={true}
                  register_key={'state'}
                  label={`State`}
                  errors={errors.state}
                  valuePre={state}
                  CustomStyle={{ pointerEvents: 'none', backgroundColor: '#E7E5E4' }}
                />

                <FormInput
                  width={32}
                  register={register}
                  register_key={'district'}
                  label={'District'}
                  type={'dropdown'}
                  defaultValuePre={district}
                  options={districtDropdownOptions}
                />
              </div>

              <div className="w-full flex justify-start space-x-4 items-center pb-6">
                <FormInput
                  width={32}
                  register={register}
                  register_key={'tehsil'}
                  label={`${isSurveyState(watchState) ? 'Taluk' : 'Tehsil'}`}
                  type={'dropdown'}
                  defaultValuePre={tehsil}
                  options={tehsilDropdownOptions}
                />

                {/* <FormInput
                  width={32}
                  register={register}
                  register_key={'city'}
                  label={'City'}
                  type={'dropdown'}
                  defaultValuePre={city}
                  options={cityDropdownOptions}
                /> */}

                <FormInput
                  width={32}
                  register={register}
                  register_key={'area'}
                  label={'Locality'}
                  type={'text'}
                  onType={handleLocalityChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Owner Details Section */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg w-full pb-2">Owner Details</span>

        {owners.map((owner, ownerIndex) => (
          <div
            className={`flex flex-col w-full flex-1 gap-4 ${ownerIndex > 0 ? 'pt-6' : 'pb-6'} border-[1px] rounded-md p-3`}
            key={owner.ownerId}>
            <div className="flex space-x-8 justify-between items-start w-full">
              <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
                <div className="w-full flex flex-col gap-4 flex-1">
                  <div className="flex justify-between space-x-4">
                    {/* Mandatory Fields */}
                    <FormInput
                      width={32}
                      register={register}
                      isRequired={true}
                      register_key={`name-${owner.ownerId}`}
                      label={'Owner Name'}
                      type={'text'}
                      onType={(val) => handleOwnerName(val, `name-${owner.ownerId}`)}
                    />

                    <FormInput
                      width={32}
                      register={register}
                      isRequired={true}
                      register_key={`phone_number-${owner.ownerId}`}
                      label={'Mobile Number'}
                      type={'text'}
                      onType={(val) => handlePhoneNumber(val, `phone_number-${owner.ownerId}`)}
                    />

                    <FormInput
                      width={32}
                      register={register}
                      isRequired={true}
                      register_key={`pan-${owner.ownerId}`}
                      label={'PAN number'}
                      type={'text'}
                      onType={(val) => handlePanChange(val, `pan-${owner.ownerId}`)}
                    />
                  </div>
                </div>

                <div className="w-full flex justify-start space-x-6 items-center">
                  {/* Optional Fields */}
                  <FormInput
                    width={32}
                    register={register}
                    register_key={`email-${owner.ownerId}`}
                    label={'Email'}
                    type={'email'}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={`aadhar-${owner.ownerId}`}
                    label={'Aadhar number'}
                    type={'text'}
                    onType={(val) => handleAadharChange(val, `aadhar-${owner.ownerId}`)}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={`diversion_order-${owner.ownerId}`}
                    label={`${isSurveyState(watchState) ? 'Conversion Order' : 'Diversion Order'}`}
                    type={'file-secondary'}
                    prefilledFiles={MultipleFiles[`diversion_order-${owner.ownerId}`] || []}
                  />
                </div>
              </div>
            </div>

            {/* Khasra Details */}
            {owner.khasras.map((khasra, khasraIndex) => (
              <div key={khasra.khasraId} className="flex flex-col gap-6 bg-gray-50 rounded-md p-3">
                <div className="w-full flex justify-between space-x-4 items-center pt-6">
                  <FormInput
                    width={20}
                    register={register}
                    register_key={`khasra_map-${khasra.khasraId}`}
                    label={`${getLandLabel(watchState)} Map`}
                    type={'file-btn'}
                    prefilledFiles={fileUrls[`khasra_map-${khasra.khasraId}`] || []}
                  />

                  <FormInput
                    width={20}
                    register={register}
                    register_key={`khasra_p1b2-${khasra.khasraId}`}
                    label={`${isSurveyState(watchState) ? 'Podi / Hissas ' : 'Khasra P1B2'}`}
                    type={'file-btn'}
                    accept={'.pdf'}
                    prefilledFiles={fileUrls[`khasra_p1b2-${khasra.khasraId}`] || []}
                  />

                  <FormInput
                    width={20}
                    register={register}
                    register_key={`land_use_map-${khasra.khasraId}`}
                    label={'Land Use Certificate'}
                    type={'file-btn'}
                    accept={'.pdf'}
                    prefilledFiles={fileUrls[`land_use_map-${khasra.khasraId}`] || []}
                  />

                  <FormInput
                    width={30}
                    register={register}
                    isRequired={true}
                    register_key={`khasra_number-${khasra.khasraId}`}
                    label={`${getLandLabel(watchState)} Number`}
                    type={'text'}
                  />
                </div>

                {/* Khasra Area Inputs */}
                <div className="w-full flex justify-start space-x-4 items-center">
                  {/* Main Area Input */}
                  <FormInput
                    width={30}
                    register={register}
                    isRequired={true}
                    register_key={`mainArea-${khasra.khasraId}`}
                    label={`${getLandLabel(watchState)} Area (${formatAreaType(totalMainAreaType)})`}
                    type={'textOnType'}
                    onType={(e) => handleMainAreaChange(owner.ownerId, khasra.khasraId, e)}
                  />

                  {/* Sub Area Input (Guntha/Cents) */}
                  <FormInput
                    width={30}
                    register={register}
                    isRequired={false}
                    register_key={`subArea-${khasra.khasraId}`}
                    label={`Sub Area${watchedSubAreaUnit !== 'none'
                        ? ` (${watchedSubAreaUnit === 'guntha' ? 'guntha' : 'cents'})`
                        : ''
                      }`}
                    type={'textOnType'}
                    disabled={watchedSubAreaUnit === 'none'}
                    CustomStyle={
                      watchedSubAreaUnit === 'none' ? { backgroundColor: '#E7E5E4' } : {}
                    }
                    onType={(e) => handleSubAreaChange(owner.ownerId, khasra.khasraId, e)}
                  />
                </div>

                {/* Remove Khasra Button */}
                {owner.khasras.length > 1 && (
                  <div className="w-full flex justify-end mt-4 self-end">
                    <span
                      className="text-stone-700 underline underline-offset-2 transition-all duration-300 hover:scale-[102%] cursor-pointer"
                      onClick={() => handleRemoveKhasraBlock(owner.ownerId, khasra.khasraId)}>
                      - Remove khasra details
                    </span>
                  </div>
                )}
              </div>
            ))}

            {/* Add Khasra Button */}
            <div className="w-full flex justify-end self-end">
              <span
                className="text-stone-700 underline underline-offset-2 transition-all duration-300 hover:scale-[102%] cursor-pointer"
                onClick={() => handleAddKhasraBlock(owner.ownerId)}>
                + Add more {getLandLabel(watchState)} details
              </span>
            </div>

            {/* Remove Owner Button */}
            {owners.length > 1 && (
              <div className="w-full flex justify-end mt-4 self-end">
                <span
                  className="text-stone-700 border-customBlue border py-2 px-4 rounded-lg transition-all duration-300 hover:scale-[102%] cursor-pointer"
                  onClick={() => handleRemoveOwnerBlock(owner.ownerId)}>
                  - Remove owner details
                </span>
              </div>
            )}
          </div>
        ))}

        {/* Add Owner Button */}
        <div className="w-full flex justify-end mt-4 self-end">
          <span
            className="text-stone-700 border-customBlue border py-2 px-4 rounded-lg transition-all duration-300 hover:scale-[102%] cursor-pointer"
            onClick={handleAddOwnerBlock}>
            + Add more owner details
          </span>
        </div>
      </div>

      {/* Additional Details Section */}
      <div className="flex flex-col justify-between items-start gap-4 pt-6">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Additional Details
        </span>

        <div className="flex flex-col w-full flex-1 gap-4">
          <div className="flex space-x-8 justify-between items-start w-full">
            <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
              <div className="w-full flex flex-col gap-4 flex-1">
                <div className="flex justify-start space-x-4">
                  <FormInput
                    width={32}
                    register={register}
                    register_key={'google_map_link'}
                    errors={errors.google_map_link}
                    label={'Google Map Link'}
                    type={'textOnType'}
                  />

                  <FormInput
                    width={32}
                    register={register}
                    register_key={'land_map'}
                    label={'Additional Documents'}
                    type={'file-secondary'}
                    prefilledFiles={MultipleFiles['land_map'] || []}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Submit
        </Button>
      </div>
    </form>
  );
};

export default EditLand;
